import {
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Badge,
  Typography,
  Box,
  Button,
  Chip,
  CircularProgress,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SmsIcon from '@mui/icons-material/Sms';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { RegistrationPlate, UserAvatar } from '@/components/common';
import { channelColors } from '@/styles/theme';
import { getConversationPartyPhone, getConversationTitle } from '@/utils/conversationParty';
import { mailboxCategoryLabelKey } from './mailboxCategory';
import type { Conversation, MessageChannel } from '@/types';
import { dateFnsLocale } from '@/utils/locale';

interface ConversationListProps {
  conversations: Conversation[];
  selectedId?: string;
  onSelect: (conversation: Conversation) => void;
  error?: boolean;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
}

const channelIcons: Record<MessageChannel, React.ReactNode> = {
  whatsapp: <WhatsAppIcon sx={{ fontSize: 14, color: channelColors.whatsapp }} />,
  sms: <SmsIcon sx={{ fontSize: 14, color: channelColors.sms }} />,
  email: <EmailIcon sx={{ fontSize: 14, color: channelColors.email }} />,
  internal: null,
  portal: <LanguageIcon sx={{ fontSize: 14, color: channelColors.portal }} />,
};

export function ConversationList({
  conversations,
  selectedId,
  onSelect,
  error,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
}: ConversationListProps) {
  const { t } = useTranslation('messages');
  const { t: tc } = useTranslation('common');

  return (
    <List disablePadding>
      {conversations.map((conversation) => {
        const isSelected = conversation.id === selectedId;
        const hasUnread = conversation.unreadCount > 0;
        const partyName = getConversationTitle(conversation, t('unknownContact'));
        const email = conversation.channel === 'email' ? conversation.email : null;
        const partyPhone = getConversationPartyPhone(conversation.customer);

        return (
          <ListItem key={conversation.id} disablePadding>
            <ListItemButton
              selected={isSelected}
              onClick={() => onSelect(conversation)}
              sx={{
                py: 1.5,
                px: 2,
                borderBottom: 1,
                borderColor: 'divider',
                backgroundColor: hasUnread && !isSelected ? 'action.hover' : undefined,
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '& .MuiTypography-root': {
                    color: 'inherit',
                  },
                  '& .MuiListItemText-secondary': {
                    color: 'rgba(255,255,255,0.7)',
                  },
                },
              }}
            >
              <ListItemAvatar>
                <Badge
                  badgeContent={conversation.unreadCount}
                  color="error"
                  invisible={!hasUnread || isSelected}
                >
                  <UserAvatar
                    name={partyName}
                    variant="rounded"
                    colorScheme={conversation.customer.customerType === 'business' ? 'business' : 'default'}
                    sx={isSelected ? { opacity: 0.8 } : undefined}
                  />
                </Badge>
              </ListItemAvatar>

              <ListItemText
                slotProps={{ secondary: { component: 'div' } }}
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography
                        variant="body2"
                        fontWeight={hasUnread ? 600 : 400}
                        noWrap
                        sx={{ maxWidth: 140 }}
                      >
                        {partyName}
                      </Typography>
                      {channelIcons[conversation.channel]}
                    </Box>
                    <Typography
                      variant="caption"
                      color={isSelected ? 'inherit' : 'text.secondary'}
                      sx={{ opacity: isSelected ? 0.7 : 1 }}
                    >
                      {formatDistanceToNow(new Date(conversation.updatedAt), {
                        addSuffix: false,
                        locale: dateFnsLocale(),
                      })}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Box sx={{ mt: 0.5 }}>
                    {email && (
                      <Typography
                        variant="caption"
                        component="div"
                        noWrap
                        sx={{
                          fontWeight: hasUnread ? 600 : 400,
                          color: isSelected ? 'inherit' : 'text.primary',
                        }}
                      >
                        {email.subject || t('emailNoSubject')}
                      </Typography>
                    )}
                    {partyPhone && partyName !== partyPhone && (
                      <Typography
                        variant="caption"
                        component="div"
                        noWrap
                        sx={{
                          color: isSelected ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                        }}
                      >
                        {partyPhone}
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      component="div"
                      noWrap
                      sx={{
                        fontWeight: hasUnread ? 500 : 400,
                        color: isSelected ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                      }}
                    >
                      {conversation.lastMessage?.direction === 'outbound' && '↩ '}
                      {conversation.lastMessage?.content}
                    </Typography>
                    {email?.mailboxCategory && (
                      <Chip
                        label={t(mailboxCategoryLabelKey[email.mailboxCategory])}
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{
                          mt: 0.5,
                          height: 18,
                          '& .MuiChip-label': { fontSize: '0.65rem', px: 0.5 },
                        }}
                      />
                    )}
                    {(conversation.staffQueueStatus === 'queued' ||
                      conversation.staffQueueStatus === 'claimed' ||
                      conversation.staffQueueStatus === 'needs_info' ||
                      conversation.staffQueueStatus === 'blocked') && (
                      <Chip
                        label={t('reviewQueue')}
                        size="small"
                        color="info"
                        sx={{
                          mt: 0.5,
                          height: 18,
                          '& .MuiChip-label': { fontSize: '0.65rem', px: 0.5 },
                        }}
                      />
                    )}
                    {conversation.requiresHuman &&
                      conversation.staffQueueStatus !== 'queued' &&
                      conversation.staffQueueStatus !== 'claimed' &&
                      conversation.staffQueueStatus !== 'needs_info' &&
                      conversation.staffQueueStatus !== 'blocked' && (
                      <Chip
                        icon={<SupportAgentIcon sx={{ fontSize: 12 }} />}
                        label={t('requiresHuman')}
                        size="small"
                        color="warning"
                        sx={{
                          mt: 0.5,
                          height: 18,
                          '& .MuiChip-label': { fontSize: '0.65rem', px: 0.5 },
                          '& .MuiChip-icon': { ml: 0.5 },
                        }}
                      />
                    )}
                    {conversation.vehicle && (
                      <RegistrationPlate
                        registrationNumber={conversation.vehicle.registrationNumber}
                        sx={{
                          mt: 0.5,
                          height: 18,
                          fontSize: '0.65rem',
                          ...(isSelected ? { bgcolor: 'rgba(255,255,255,0.2)', color: 'inherit', borderColor: 'rgba(255,255,255,0.3)' } : {}),
                        }}
                      />
                    )}
                  </Box>
                }
              />
            </ListItemButton>
          </ListItem>
        );
      })}

      {conversations.length === 0 && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          {error ? (
            <>
              <ErrorOutlineIcon sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
              <Typography color="error.main">{t('conversationsLoadFailed')}</Typography>
            </>
          ) : (
            <Typography color="text.secondary">{t('noConversations')}</Typography>
          )}
        </Box>
      )}

      {hasMore && onLoadMore && conversations.length > 0 && (
        <Box sx={{ p: 1.5, textAlign: 'center' }}>
          <Button
            size="small"
            onClick={onLoadMore}
            disabled={loadingMore}
            startIcon={loadingMore ? <CircularProgress size={14} /> : undefined}
          >
            {tc('loadMore')}
          </Button>
        </Box>
      )}
    </List>
  );
}
